/**
 * Foods that are counted rather than weighed.
 *
 * Nobody puts an egg on a scale. They put two eggs in a pan — and a tracker
 * that can only be told "120 g" is asking them to do arithmetic about a thing
 * they had already counted, which is the kind of small friction that ends with
 * the egg not being logged at all.
 *
 * Grams stay the unit of record underneath: every macro in the app is per 100 g
 * and every total is a weight, so a piece is a way of saying a weight rather
 * than a second unit to carry around. What one piece weighs lives on the food
 * (`pieceWeightG`), because it is a fact about the product and not about the
 * meal.
 */

import { round } from './macros.js'

/** How many of them a weight is. Rounded to a half, which is how eggs go. */
export function piecesOf(grams: number, pieceWeightG: number): number {
  if (pieceWeightG <= 0) return 0
  return round(Math.round((grams / pieceWeightG) * 2) / 2, 1)
}

/** What that many of them weigh. */
export function gramsOfPieces(pieces: number, pieceWeightG: number): number {
  return round(pieces * pieceWeightG, 1)
}

/**
 * "2 vnt.", and "1,5 vnt." when somebody used half an egg.
 *
 * `vnt.` rather than the noun itself: Lithuanian would need three forms of it
 * (1 kiaušinis, 2 kiaušiniai, 11 kiaušinių) and the abbreviation is both what
 * every label in the shop uses and the only form that works for every food.
 */
export function piecesLabel(pieces: number): string {
  const shown = round(pieces, 1)
  return `${String(shown).replace('.', ',')} vnt.`
}

/** The counts worth one tap. Eggs come in twos and threes, not in 150s. */
export const PIECE_PRESETS: readonly number[] = [1, 2, 3, 4, 6]
